import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { devLogin } from '../services/api';
import BrandLogo from '../components/wizard/BrandLogo';

const IS_LOCAL = !process.env.REACT_APP_PDS_API_URL ||
  process.env.REACT_APP_PDS_API_URL.includes('localhost');

/**
 * LoginPage handles two cases:
 *
 * 1. Client arrives via magic link: `/login?token=<raw_token>` — the token
 *    is read from the URL and exchanged for a JWT automatically.
 * 2. Client navigates directly without a token and pastes their access
 *    link or token manually.
 */
const CLIENT_TYPE_KEY = 'pds_client_type';

export default function LoginPage() {
  const { client, login, logout, error: authError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  const clientTypeFromUrl = searchParams.get('client_type') || '';

  const [manualToken, setManualToken] = useState('');
  const [localError, setLocalError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [autoLogging, setAutoLogging] = useState(false);

  // Persist client_type from the magic link so the wizard can use it
  // before the progress endpoint responds.
  const [clientType, setClientType] = useState(
    () => clientTypeFromUrl || localStorage.getItem(CLIENT_TYPE_KEY) || 'hff',
  );

  useEffect(() => {
    if (clientTypeFromUrl) {
      localStorage.setItem(CLIENT_TYPE_KEY, clientTypeFromUrl);
      setClientType(clientTypeFromUrl);
    }
  }, [clientTypeFromUrl]);

  const themeClass = clientType === 'rebellionaire' ? 'theme-rebellionaire' : '';

  useEffect(() => {
    // If a token is present in the URL, always exchange it first instead of
    // trusting whatever token is in localStorage.
    if (client && !tokenFromUrl) {
      navigate('/wizard', { replace: true });
    }
  }, [client, navigate, tokenFromUrl]);

  useEffect(() => {
    const token = tokenFromUrl;
    if (!token) return;

    setAutoLogging(true);
    logout();
    login(token)
      .then(() => navigate('/wizard', { replace: true }))
      .catch((err) => {
        setLocalError(err.message);
        setAutoLogging(false);
      });
  }, [tokenFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(e) {
    e.preventDefault();
    setLocalError('');

    let rawToken = manualToken.trim();
    try {
      const url = new URL(rawToken);
      rawToken = url.searchParams.get('token') || rawToken;
      const ct = url.searchParams.get('client_type');
      if (ct) {
        localStorage.setItem(CLIENT_TYPE_KEY, ct);
        setClientType(ct);
      }
    } catch {
      // Not a URL — treat as raw token
    }

    if (!rawToken) {
      setLocalError('Please enter your access link or token.');
      return;
    }

    setSubmitting(true);
    try {
      await login(rawToken);
      navigate('/wizard', { replace: true });
    } catch (err) {
      setLocalError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (autoLogging) {
    return (
      <div className={`cashflow-login-bg d-flex justify-content-center align-items-center vh-100 ${themeClass}`}>
        <div className="text-center">
          <div className="spinner-border text-danger mb-3" role="status" />
          <p className="text-muted">Verifying your access link…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`cashflow-login-bg d-flex justify-content-center align-items-center vh-100 ${themeClass}`}>
      <div className="cashflow-login-card card shadow-sm p-4" style={{ maxWidth: 480, width: '100%' }}>
        <div className="text-center mb-4">
          <BrandLogo size={56} className="mb-2" />
          <h1 className="h4 fw-bold cashflow-brand-text">Personal Data Sheet</h1>
          <p className="text-muted small mb-0">Secure Client Portal</p>
        </div>

        {(localError || authError) && (
          <div className="alert alert-danger d-flex align-items-center gap-2 py-2">
            <i className="bi bi-exclamation-triangle-fill" />
            <span>{localError || authError}</span>
          </div>
        )}

        <p className="text-muted small mb-3">
          Your advisor sent you a personal access link. Click that link to
          open this page automatically, or paste it below.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="tokenInput" className="form-label fw-semibold">
              Access Link or Token
            </label>
            <input
              id="tokenInput"
              type="text"
              className="form-control"
              placeholder="Paste your link or token here…"
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <button
            type="submit"
            className="btn btn-danger w-100 py-2 fw-semibold"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Verifying…
              </>
            ) : (
              'Continue to My Personal Data Sheet'
            )}
          </button>
        </form>

        <hr className="my-3" />
        <p className="text-muted text-center small mb-0">
          Don't have a link? Contact your financial advisor to receive one.
        </p>

        {IS_LOCAL && (
          <>
            <hr className="my-3" />
            <p className="text-muted text-center small mb-2 fw-semibold">Dev Login (local only)</p>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary flex-fill btn-sm"
                disabled={submitting}
                onClick={async () => {
                  setLocalError('');
                  setSubmitting(true);
                  try {
                    const data = await devLogin('hff');
                    sessionStorage.setItem('pds_token', data.access_token);
                    localStorage.removeItem('pds_token');
                    localStorage.setItem(CLIENT_TYPE_KEY, 'hff');
                    window.location.href = '/wizard';
                  } catch (err) {
                    setLocalError('Dev login failed — is the backend running on port 8019?');
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                <i className="bi bi-code-slash me-1" />
                HFF Client
              </button>
              <button
                className="btn btn-outline-warning flex-fill btn-sm"
                disabled={submitting}
                onClick={async () => {
                  setLocalError('');
                  setSubmitting(true);
                  try {
                    const data = await devLogin('rebellionaire');
                    sessionStorage.setItem('pds_token', data.access_token);
                    localStorage.removeItem('pds_token');
                    localStorage.setItem(CLIENT_TYPE_KEY, 'rebellionaire');
                    window.location.href = '/wizard';
                  } catch (err) {
                    setLocalError('Dev login failed — is the backend running on port 8019?');
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                <i className="bi bi-lightning-fill me-1" />
                Rebellionaire
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
